import { ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  apiSlice,
  resetItem,
  selectProductTag,
  setFont,
  setIcon,
  setMaterial,
  setPalette,
  setProductId,
  setProductTag,
  setProductVdpCode,
  setSlots,
  setStyle,
  setTextField,
  setTextFields,
  updateLabelShape
} from 'configurator-ui-shared';

import { RootState } from '../../../store';
import { Product } from '../../../types';
import { CartItemState } from '../../app/Configurator';
import {
  getApiCartItem,
  getShopifyCart,
  resolveMultiConfigSession
} from '../../cart/utils';

import {
  captureCurrentSnapshot,
  generateConfigId,
  hydrateSessionFromEdit,
  loadSnapshot
} from './multiConfigSlice';
import { EditSessionLinkedUpsell } from '../types';

// The extra-arg type param has to match this file's own ThunkAction
// declarations (unknown) - RootState's own store dispatch type
// (ConfiguratorDispatch) is typed with a different (undefined) extra-arg,
// which TypeScript treats as an unrelated type despite both being
// functionally compatible at runtime.
type EditSessionDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

// Rebuilds item.properties.fields directly instead of going through
// itemSlice's debounced syncFields thunk (which would otherwise leave
// properties.fields stale for however long the debounce takes, right when
// we're about to capture a snapshot of it) - mirrors what syncFieldsInner
// does internally, minus the debounce.
function buildFieldItemState(
  cartItemState: CartItemState,
  apiProduct: Product
) {
  const textFieldDefs = apiProduct.labels?.flatMap(label =>
    label.variants.flatMap(variant => variant.text_fields)
  );

  const fieldItemState: Record<
    string,
    {
      field_id: string;
      display_name: string | undefined;
      display_name_2?: string | null;
      value: string;
    }
  > = {};

  for (const field of cartItemState.text_fields) {
    const fieldDefinition = textFieldDefs?.find(
      def => def.text_field_id === field.text_field_id
    );
    fieldItemState[field.text_field_id] = {
      field_id: field.text_field_id,
      display_name: fieldDefinition?.display_name,
      display_name_2: fieldDefinition?.display_name_2,
      value: field.value
    };
  }

  return fieldItemState;
}

// Loads one saved cart item's data into the live editor state. Deliberately
// duplicates (rather than shares) the lookups Configurator.tsx's six
// single-item edit hydration effects perform, in the same order, so this
// produces identical results without risking any change to that proven,
// already-working path. Used to hydrate each sibling configuration in turn
// so its resulting state can be captured as a ConfigSnapshot before moving
// on to the next.
async function hydrateLiveItemFromCartItemState(
  dispatch: EditSessionDispatch,
  cartItemState: CartItemState,
  apiProduct: Product,
  productTag: string
): Promise<void> {
  // resetItem wipes item.properties back to its initial (empty) values -
  // productId/productTag/vdpcode included - so they have to be restored
  // explicitly here, exactly like the app's own initial-load effect does
  // (Configurator.tsx's handleShowReactApp). Omitting this left every
  // reconstructed snapshot with an empty productId, which getCartErrors
  // then reports as "Product ID required" the moment the shopper tries to
  // save.
  dispatch(resetItem());
  dispatch(setProductTag(productTag));
  dispatch(setProductId(apiProduct.product_id));
  dispatch(setProductVdpCode(apiProduct.vdpcode));

  const style = apiProduct.styles.find(
    style => cartItemState.style_id === style.style_id
  );
  if (style) dispatch(setStyle(style));

  const palette = apiProduct.palettes.find(
    palette => cartItemState.palette_id === palette.palette_id
  );
  if (palette) dispatch(setPalette(palette));

  const font = apiProduct.fonts.find(
    font => cartItemState.font_id === font.font_id
  );
  if (font) dispatch(setFont(font));

  const icon = apiProduct.icons.find(
    icon => cartItemState.icon_id === icon.icon_id
  );
  if (icon) dispatch(setIcon(icon));

  cartItemState.text_fields.forEach(field => {
    dispatch(setTextField({ fieldId: field.text_field_id, text: field.value }));
  });
  dispatch(setTextFields(buildFieldItemState(cartItemState, apiProduct)));

  const usedMaterialIds = new Set(
    cartItemState.label_slots.map(slot => slot.material_id)
  );
  let selectedMaterialId: string | undefined;
  if (usedMaterialIds.size === 1) {
    selectedMaterialId = usedMaterialIds.values().next().value;
  } else {
    for (const label of apiProduct.labels) {
      const labelHasMultipleMaterialOptions = label.material_ids.length > 1;
      if (!labelHasMultipleMaterialOptions) continue;

      const matchingSlot = cartItemState.label_slots.find(
        slot => slot.label_id === label.label_id
      );
      const isValidSlot =
        matchingSlot && usedMaterialIds.has(matchingSlot.material_id);

      if (isValidSlot) {
        selectedMaterialId = matchingSlot.material_id;
        break;
      }
    }
    if (!selectedMaterialId) {
      selectedMaterialId = usedMaterialIds.values().next().value;
    }
  }
  const selectedMaterial = apiProduct.materials.find(
    material => material.material_id === selectedMaterialId
  );
  if (selectedMaterial) dispatch(setMaterial(selectedMaterial));

  cartItemState.label_slots.forEach(field => {
    dispatch(
      setSlots({
        slotId: field.slot_id,
        value: {
          labelId: field.label_id,
          variantId: field.label_variant_id,
          materialId: field.material_id
        }
      })
    );
  });
  if (cartItemState.label_slots.length === 1) {
    await dispatch(updateLabelShape(cartItemState.label_slots[0].label_id));
  }
}

// Reconstructs full multi-config session state from an existing cart line:
// resolves the session's sibling cart_item_ids, reads the live Shopify cart
// once to learn each sibling's cart line key and _multi_config_index
// (neither is stored in customer-api - both are Shopify-cart-only
// properties stamped at original add-to-cart time), fetches each sibling's
// saved configuration, and hydrates+snapshots each one in turn so the
// switcher/add-to-cart flow can treat this exactly like a fresh session.
export const reconstructMultiConfigSessionForEdit =
  (
    primaryCartItemId: string,
    primaryCartItemState: CartItemState
  ): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const sessionId = primaryCartItemState.multi_config_session_id;
    if (!sessionId) return;

    const [session, shopifyCart] = await Promise.all([
      resolveMultiConfigSession(sessionId),
      getShopifyCart()
    ]);

    const lineByCartItemId = new Map<string, { key: string; index: number }>();
    for (const line of shopifyCart.items) {
      const cartItemId = line.properties?._cart_item_id;
      if (!cartItemId) continue;
      const index = Number(line.properties?._multi_config_index);
      lineByCartItemId.set(cartItemId, {
        key: line.key,
        index: Number.isFinite(index) ? index : Number.MAX_SAFE_INTEGER
      });
    }

    const siblingIds = session.cart_item_ids.filter(
      id => id !== primaryCartItemId
    );
    const siblingStates = await Promise.all(
      siblingIds.map(async id => ({ id, state: await getApiCartItem(id) }))
    );

    const allEntries = [
      { id: primaryCartItemId, state: primaryCartItemState },
      ...siblingStates
    ]
      .map(entry => ({
        ...entry,
        line: lineByCartItemId.get(entry.id)
      }))
      // A sibling whose Shopify line can't be found (e.g. removed from the
      // cart through some other means since it was added) can't be safely
      // edited/re-saved as part of this session - drop it rather than act
      // on stale data.
      .filter(
        (
          entry
        ): entry is typeof entry & { line: { key: string; index: number } } =>
          entry.line !== undefined
      )
      .sort((a, b) => a.line.index - b.line.index);

    if (allEntries.length === 0) return;

    const productTag = selectProductTag(getState());
    const apiProduct = apiSlice.endpoints.getProduct.select(productTag)(
      getState()
    )?.data;
    if (!apiProduct) return;

    const configs: Array<{
      id: string;
      snapshot: ReturnType<typeof captureCurrentSnapshot>;
      cartItemId: string;
      key: string;
    }> = [];

    for (const entry of allEntries) {
      await hydrateLiveItemFromCartItemState(
        dispatch,
        entry.state,
        apiProduct,
        productTag
      );
      configs.push({
        id: generateConfigId(),
        snapshot: captureCurrentSnapshot(getState()),
        cartItemId: entry.id,
        key: entry.line.key
      });
    }

    const primaryConfig =
      configs.find(config => config.cartItemId === primaryCartItemId) ??
      configs[0];

    // Any Shopify cart line whose _parent_item_id points at one of this
    // session's cart_item_ids is an upsell tied to that configuration -
    // found by scanning the same live cart fetched above, since
    // customer-api has no concept of this link either (it's stamped as a
    // Shopify line property at add-time, same as _multi_config_index).
    const configIdByCartItemId = new Map(
      configs.map(({ id, cartItemId }) => [cartItemId, id])
    );
    const upsellsByConfigId: Record<string, EditSessionLinkedUpsell[]> = {};
    for (const line of shopifyCart.items) {
      const parentCartItemId = line.properties?._parent_item_id;
      const upsellCartItemId = line.properties?._cart_item_id;
      if (!parentCartItemId || !upsellCartItemId) continue;

      const configId = configIdByCartItemId.get(parentCartItemId);
      if (!configId) continue;

      if (!upsellsByConfigId[configId]) upsellsByConfigId[configId] = [];
      upsellsByConfigId[configId].push({
        cartItemId: upsellCartItemId,
        key: line.key,
        variantId: line.id,
        properties: line.properties
      });
    }

    dispatch(
      hydrateSessionFromEdit({
        configs: configs.map(({ id, snapshot }) => ({ id, snapshot })),
        activeConfigId: primaryConfig.id,
        maxConfigs: apiProduct.multi_config_max_configs ?? null,
        minConfigs: apiProduct.multi_config_min_configs ?? 1,
        shopifyKeysByConfigId: Object.fromEntries(
          configs.map(({ id, key }) => [id, key])
        ),
        cartItemIdsByConfigId: Object.fromEntries(
          configs.map(({ id, cartItemId }) => [id, cartItemId])
        ),
        multiConfigSessionId: sessionId,
        upsellsByConfigId
      })
    );

    // Sync live editor state to match the designated-active (originally
    // clicked-into) configuration - the hydration loop above leaves live
    // state on whichever configuration was processed last, which won't
    // generally be this one.
    dispatch(loadSnapshot(primaryConfig.snapshot));
  };
